import { Component, HostListener, Inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { SearchBarService } from 'src/app/shared/components/search-bar/search-bar.service';
import { TableColumnTypeEnum } from 'src/app/shared/components/table/shared/enums';
import { TableConfig, TableFilters } from 'src/app/shared/components/table/shared/models';
import { BasicService } from 'src/app/shared/services/basic.service';

import { CustomRouter } from 'src/admin-front/ajs-upgraded-providers';
import { AlcmeonAiService } from '../alcmeon-ai.service';
import { AiAgentModeEnum, AiAgentTypeEnum } from '../shared/enums';
import { AiAgent } from '../shared/models';

@Component({
  selector: 'app-ai-agents',
  templateUrl: './ai-agents.component.html',
  styleUrls: ['./ai-agents.component.scss'],
})
export class AiAgentsComponent implements OnInit, OnDestroy {
  config: TableConfig = {
    displayedColumns: [
      'name',
      'type',
      'isActive',
      'mode',
      'restrictedToTeamsList',
      'knowledgeDocumentsUsedTpl',
      'updatedAt',
      'actions',
    ],
    columns: [
      {
        name: 'name',
        translationKey: 'parameters/alcmeon-ai/agents/table/col/name',
        property: 'nameLink',
        sortEnabled: true,
        type: TableColumnTypeEnum.LINK,
      },

      {
        name: 'type',
        translationKey: 'parameters/alcmeon-ai/agents/table/col/type',
        property: 'typeString',
        sortEnabled: true,
      },

      {
        name: 'isActive',
        translationKey: 'parameters/alcmeon-ai/agents/table/col/activation',
        property: 'isActive',
        sortEnabled: true,
        type: TableColumnTypeEnum.ACTIVE,
      },

      {
        name: 'mode',
        translationKey: 'parameters/alcmeon-ai/agents/table/col/used',
        property: 'modeString',
        sortEnabled: true,
      },

      {
        name: 'restrictedToTeamsList',
        translationKey: 'parameters/alcmeon-ai/agents/table/col/teams',
        property: 'restrictedToTeamsList',
        type: TableColumnTypeEnum.LIST,
      },

      {
        name: 'knowledgeDocumentsUsedTpl',
        translationKey: 'parameters/alcmeon-ai/agents/table/col/docs',
        property: 'knowledgeDocumentsUsedTpl',
        type: TableColumnTypeEnum.TEMPLATE_REF,
      },

      {
        name: 'updatedAt',
        translationKey: 'parameters/alcmeon-ai/agents/table/col/edition-date',
        property: 'updatedAt',
        type: TableColumnTypeEnum.DATE,
        sortEnabled: true,
      },
    ],
    actions: [
      {
        tooltip: 'delete',
        clickFn: (agent: AiAgent) => this.deleteAgent(agent.id),
        icon: 'delete',
        color: 'warn',
      },
    ],
  };

  filters: TableFilters = {};

  knowledgeDocsCounters: { [key: number]: number } = {};
  knowledgeDocsCountersLoading = true;
  routingTagsMap: Map<number, string> = new Map();

  private subscriptions = new Subscription();

  @ViewChild('knowledgeDocumentsCounter') knowledgeDocumentsCounterTemplateRef: TemplateRef<any>;

  @HostListener('window:resize')
  onWindowResizeUpdateActionsPosition() {
    this.searchBarService.checkScrollPosition();
  }

  constructor(
    private alcmeonAiService: AlcmeonAiService,
    private alcmeonContextService: AlcmeonContextService,
    private basicService: BasicService,
    private customRouter: CustomRouter,
    private dialog: MatDialog,
    private searchBarService: SearchBarService,
    @Inject('TopToolbarFactory') private topToolbarService: any
  ) {}

  ngOnInit() {
    this.topToolbarService.setTitle(
      `${this.basicService.translate('parameters/alcmeon-ai/title')} › ${this.basicService.translate(
        'parameters/alcmeon-ai/tab/agents'
      )}`,
      null
    );
    this.searchBarService.sort = { active: 'name', direction: 'asc' };
    this.searchBarService.referer$ = this.alcmeonAiService.getAiAgents;
    this.searchBarService.formatter = this.formatItem;
    this.searchBarService.submitQuery();

    this.buildRoutingTagMap();

    // make a parallel call (because async) to obtain counters of knowledge docs
    // make this call only one time, as we got all counters (all pages of pagination) in one call
    this.alcmeonAiService.getKnowledgeDocsCountersForAiAgents().subscribe(counters => {
      this.knowledgeDocsCounters = counters;

      // apply counters in current results if they were faster to obtain
      (this.searchBarService.itemsBS.value || []).forEach((aiAgent: AiAgent) => {
        this.applyKnowledgeDocsCounters(aiAgent);
      });

      this.knowledgeDocsCountersLoading = false;
    });
  }

  buildRoutingTagMap() {
    const user = this.alcmeonContextService.self;
    user.routing_tags.forEach(tag => {
      this.routingTagsMap.set(tag.id, tag.name);
    });
  }

  formatItem = (aiAgent: AiAgent) => {
    const { mode, type, name, id } = aiAgent;
    aiAgent.modeString = this.basicService.translate(
      `parameters/alcmeon-ai/agents/table/col/${mode === AiAgentModeEnum.aissitant ? 'ai-ssistant' : 'ai-bots'}`
    );

    aiAgent.typeString = this.basicService.translate(
      type
        ? 'parameters/alcmeon-ai/agents/table/third-party/third-party'
        : 'parameters/alcmeon-ai/agents/table/third-party/gaia'
    );

    const aiAgentHref = this.basicService.fullURLForRoutePath(
      `/#/${this.alcmeonContextService.companyId}/fuse/parameters/alcmeon-ai/agents/edit/${id}`
    );
    aiAgent.nameLink = {
      text: name,
      href: aiAgentHref,
      newTab: false,
    };

    aiAgent.restrictedToTeamsList = aiAgent.restrictedToTeams.map(teamId => {
      return this.routingTagsMap.get(teamId);
    });

    this.applyKnowledgeDocsCounters(aiAgent);
  };

  applyKnowledgeDocsCounters(aiAgent: AiAgent) {
    if (aiAgent.type === AiAgentTypeEnum.GAIA) {
      aiAgent.knowledgeDocumentsUsedTpl = {
        templateRef: this.knowledgeDocumentsCounterTemplateRef,
        context: { counter: this.knowledgeDocsCounters[aiAgent.id] || 0 },
      };
    } else {
      aiAgent.knowledgeDocumentsUsedTpl = {
        templateRef: this.knowledgeDocumentsCounterTemplateRef,
        context: { counter: this.basicService.translate('not_applicable') },
      };
    }
  }

  newAgent = () => {
    this.customRouter.go('auth.alcmeon.fuse.parameters.alcmeon-ai.agents.create');
  };

  deleteAgent(id: number) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        description: this.basicService.translate('parameters/alcmeon-ai/agents/delete-confirmation'),
        confirmBtnText: this.basicService.translate('ok'),
        cancelBtnText: this.basicService.translate('cancel'),
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.basicService.showSpinner();
        this.alcmeonAiService.deleteAiAgent(id).subscribe(() => {
          this.searchBarService.submitQuery();
          this.basicService.toastSuccess('parameters/alcmeon-ai/agents/delete-success');
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
    this.searchBarService.onDidLeave();
  }
}
